import React, { useState, useEffect, useCallback } from "react";
import { Container, Row, Col, Button, Form, Table, Spinner, Card } from "react-bootstrap";
import { Calendar } from "react-calendar"
import 'react-calendar/dist/Calendar.css'
import "./AdminScreen.scss";

import instance from "../../../services/api";

const AdminScreen = () => {
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [bookedLessons, setBookedLessons] = useState([]);
    const [comment, setComment] = useState('');

    const [username, setUsername] = useState('');
    const [isGroup, setIsGroup] = useState(false);
    const [ctFullName, setCtFullName] = useState('');
    const [ctPosition, setCtPosition] = useState('');
    const [ctPhone, setCtPhone] = useState('');
    const [ctEmail, setCtEmail] = useState('');
    const [orgName, setOrgName] = useState('');
    const [orgAddress, setOrgAddress] = useState('');
    const [orgSchools, setOrgSchools] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    const [organisationAccounts, setOrganisationAccounts] = useState([]);
    const [selectedOrgUsername, setSelectedOrgUsername] = useState('');
    const [newOrgPassword, setNewOrgPassword] = useState('');
    const [confirmNewOrgPassword, setConfirmNewOrgPassword] = useState('');
    const [organisationAccountsLoading, setOrganisationAccountsLoading] = useState(true);

    const [studentEmail, setStudentEmail] = useState('');
    const [studentFullName, setStudentFullName] = useState('');
    const [studentYearLevelGroup, setStudentYearLevelGroup] = useState('5-6');
    const [studentAccountMode, setStudentAccountMode] = useState('create');
    const [studentAccounts, setStudentAccounts] = useState([]);
    const [subscriptionsLoading, setSubscriptionsLoading] = useState(true);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const fetchStudentAccounts = useCallback(async () => {
        setSubscriptionsLoading(true);
        await instance.get("/admin/student-accounts")
            .then((response) => {
                const students = Array.isArray(response.data) ? response.data : [];
                setStudentAccounts(students);
            })
            .catch((err) => {
                console.error(err);
            })
            .finally(() => {
                setSubscriptionsLoading(false);
            });
    }, []);

    useEffect(() => {
        fetchStudentAccounts();
    }, [fetchStudentAccounts]);

    const fetchOrganisationAccounts = useCallback(async () => {
        setOrganisationAccountsLoading(true);
        await instance.get("/admin/organisation-accounts")
            .then((response) => {
                const orgAccounts = Array.isArray(response.data) ? response.data : [];
                setOrganisationAccounts(orgAccounts);
                setSelectedOrgUsername((currentUsername) => currentUsername || (orgAccounts[0]?.username || ""));
            })
            .catch((err) => {
                console.error(err);
            })
            .finally(() => {
                setOrganisationAccountsLoading(false);
            });
    }, []);

    useEffect(() => {
        fetchOrganisationAccounts();
    }, [fetchOrganisationAccounts]);

    useEffect(() => {
        const fetchScheduleByDate = async () => {
            const offsetDate = new Date(selectedDate.getTime() - selectedDate.getTimezoneOffset() * 60 * 1000);
            const newDate = offsetDate.toISOString().split('T')[0];
            await instance.get(`/admin/get-schedule/${encodeURIComponent(newDate)}`)
                .then(( response ) => {
                    const lessons = [];
                    response.data.forEach((lesson) => {
                        lessons.push(lesson);
                    })

                    setBookedLessons(lessons);
                })
                .catch(( err ) => {
                    console.error(err);
                })
        };

        fetchScheduleByDate();
    }, [selectedDate]);

    const blockTimeHandler = async ( time ) => {
        const [hours, minutes] = time.split(":");
        let chosenDate;
        chosenDate = selectedDate;

        chosenDate.setHours(hours);
        chosenDate.setMinutes(minutes);
        chosenDate.setSeconds(0);
        chosenDate.setMilliseconds(0);

        const offsetDateTime = new Date(selectedDate.getTime() - selectedDate.getTimezoneOffset() * 60 * 1000);
        const dateTime = offsetDateTime.toISOString();

        await instance.post("/admin/block-time", {
            dateTime, comment
        }).then(() => {
            window.alert("Time blocked!");
        }).catch(() => {
            window.alert("Block failed!");
        })
    }

    const unblockTimeHandler = async ( lesson ) => {
        await instance.delete("/admin/block-time/" + lesson._id).then(() => {
            setBookedLessons(bookedLessons.filter((item) => item._id !== lesson._id));
            window.alert("Time unblocked!");
        }).catch(() => {
            window.alert("Unblock failed!");
        });
    };

    const submitHandler = async (e) => {
        e.preventDefault();

        if ( password !== confirmPassword ) {
            setPassword("");
            setConfirmPassword("");
            window.alert("Passwords do not match.");
            return;
        }

        if ( password.length < 6 ) {
            setPassword("");
            setConfirmPassword("");
            window.alert("Password must be at least 6 characters long.");
            return;
        }

        await instance.post("/admin/org-register", {
            username,
            password,
            isGroup,
            ctFullName,
            ctPosition,
            ctPhone,
            ctEmail,
            orgName,
            orgAddress,
            orgSchools,
        }).then(() => {
            window.alert("Successfully registered new organisation/school!");
        }).catch(() => {
            window.alert("Failed to register new organisation/school!");
        });
    };

    const createStudentAccountHandler = async (e) => {
        e.preventDefault();

        await instance.post("/admin/create-student-account", {
            fullName: studentFullName,
            email: studentEmail,
            studentCourseYearLevelGroup: studentYearLevelGroup,
            accountMode: studentAccountMode,
        }).then(() => {
            setStudentFullName('');
            setStudentEmail('');
            setStudentYearLevelGroup('5-6');
            setStudentAccountMode('existing');
            window.alert(studentAccountMode === 'create' ? "Student account created successfully and credentials emailed." : "Student subscription granted successfully.");
            fetchStudentAccounts();
        }).catch((error) => {
            const errorMessage = error?.response?.data?.error || "Failed to process student account.";
            window.alert(errorMessage);
        });
    };

    const changeOrgPasswordHandler = async (e) => {
        e.preventDefault();

        if ( newOrgPassword !== confirmNewOrgPassword ) {
            setNewOrgPassword("");
            setConfirmNewOrgPassword("");
            window.alert("Passwords do not match.");
            return;
        }

        if ( newOrgPassword.length < 6 ) {
            setNewOrgPassword("");
            setConfirmNewOrgPassword("");
            window.alert("Password must be at least 6 characters long.");
            return;
        }

        await instance.post("/admin/change-org-password", {
            username: selectedOrgUsername,
            newPassword: newOrgPassword,
        }).then(() => {
            setNewOrgPassword("");
            setConfirmNewOrgPassword("");
            window.alert("Organisation password updated successfully.");
        }).catch((error) => {
            const errorMessage = error?.response?.data?.error || "Failed to update organisation password.";
            window.alert(errorMessage);
        });
    };

    return (
        <Container className="admin-screen py-5">
            <section className="admin-section">
                <div className="admin-section-header">
                    <h3>Scheduler</h3>
                    <p>Choose a date, block unavailable times, and review confirmed bookings in one place.</p>
                </div>
                <Row className="g-4 align-items-stretch">
                    <Col lg={5}>
                        <Card className="admin-card h-100">
                            <Card.Body>
                                <h5 className="mb-3">Calendar</h5>
                                <Calendar onChange={setSelectedDate} value={selectedDate} className="mb-4" />
                                <Form.Group controlId="formBlockedTimeComment" className="mb-0">
                                    <Form.Label>Comment for blocked time</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={4}
                                        value={comment}
                                        onChange={(e) => setComment(e.target.value)}
                                        placeholder="Optional note for this blocked time"
                                    />
                                </Form.Group>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col lg={7}>
                        <Card className="admin-card h-100">
                            <Card.Body>
                                <h5 className="mb-3">Schedule</h5>
                                <ScheduleComponent blockTimeHandler={blockTimeHandler} unblockTimeHandler={unblockTimeHandler} bookedLessons={bookedLessons} />
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </section>

            <section className="admin-section">
                <div className="admin-section-header">
                    <h3>Professional Development School/Organisation Registration</h3>
                    <p>Create organisation accounts with contact and school details for program access.</p>
                </div>
                <Card className="admin-card">
                    <Card.Body>
                        <Form onSubmit={submitHandler}>
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3" controlId="formUsername">
                                        <Form.Label>Account Username</Form.Label>
                                        <Form.Control required placeholder="Enter username" value={username} onChange={(e) => setUsername(e.target.value)} />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3" controlId="formCtFullName">
                                        <Form.Label>Contact Full Name</Form.Label>
                                        <Form.Control required placeholder="Contact full name" value={ctFullName} onChange={(e) => setCtFullName(e.target.value)} />
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3" controlId="formPassword">
                                        <Form.Label>Password</Form.Label>
                                        <Form.Control type="password" required autoComplete="true" placeholder="Enter password" value={password} onChange={(e) => setPassword(e.target.value)} />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3" controlId="formConfirmPassword">
                                        <Form.Label>Confirm Password</Form.Label>
                                        <Form.Control type="password" required autoComplete="true" placeholder="Confirm password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3" controlId="formCtPosition">
                                        <Form.Label>Contact Position</Form.Label>
                                        <Form.Control required placeholder="Contact position" value={ctPosition} onChange={(e) => setCtPosition(e.target.value)} />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3" controlId="formCtPhone">
                                        <Form.Label>Contact Phone</Form.Label>
                                        <Form.Control required placeholder="Contact phone" value={ctPhone} onChange={(e) => setCtPhone(e.target.value)} />
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3" controlId="formCtEmail">
                                        <Form.Label>Contact Email</Form.Label>
                                        <Form.Control type="email" required placeholder="Contact email" value={ctEmail} onChange={(e) => setCtEmail(e.target.value)} />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3" controlId="formOrgName">
                                        <Form.Label>Organisation Name</Form.Label>
                                        <Form.Control required placeholder="Organisation name" value={orgName} onChange={(e) => setOrgName(e.target.value)} />
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3" controlId="formOrgAddress">
                                        <Form.Label>Organisation Address</Form.Label>
                                        <Form.Control required placeholder="Organisation address" value={orgAddress} onChange={(e) => setOrgAddress(e.target.value)} />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3" controlId="formOrgSchools">
                                        <Form.Label>Schools List (if multiple)</Form.Label>
                                        <Form.Control placeholder="School 1, School 2, School 3" value={orgSchools} onChange={(e) => setOrgSchools(e.target.value)} />
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Form.Group className="mb-4" controlId="formGroup">
                                <Form.Check type="checkbox" label="This account manages multiple schools" checked={isGroup} onChange={(e) => setIsGroup(e.target.checked)} />
                            </Form.Group>

                            <Button variant="primary" type="submit">Confirm Details and Register</Button>
                        </Form>
                    </Card.Body>
                </Card>
            </section>

            <section className="admin-section">
                <div className="admin-section-header">
                    <h3>Change Organisation Account Password</h3>
                    <p>Choose an organisation account and set a new password.</p>
                </div>
                <Card className="admin-card">
                    <Card.Body>
                        <Form onSubmit={changeOrgPasswordHandler}>
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3" controlId="formSelectedOrgAccount">
                                        <Form.Label>Organisation Account Username</Form.Label>
                                        <Form.Select
                                            required
                                            value={selectedOrgUsername}
                                            onChange={(e) => setSelectedOrgUsername(e.target.value)}
                                            disabled={organisationAccountsLoading || organisationAccounts.length === 0}
                                        >
                                            {organisationAccounts.length === 0 ? (
                                                <option value="">No organisation accounts found</option>
                                            ) : (
                                                organisationAccounts.map((orgAccount) => (
                                                    <option key={orgAccount.username} value={orgAccount.username}>
                                                        {orgAccount.username}
                                                    </option>
                                                ))
                                            )}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3" controlId="formNewOrgPassword">
                                        <Form.Label>New Password</Form.Label>
                                        <Form.Control
                                            type="password"
                                            required
                                            autoComplete="true"
                                            placeholder="Enter new password"
                                            value={newOrgPassword}
                                            onChange={(e) => setNewOrgPassword(e.target.value)}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-4" controlId="formConfirmNewOrgPassword">
                                        <Form.Label>Confirm New Password</Form.Label>
                                        <Form.Control
                                            type="password"
                                            required
                                            autoComplete="true"
                                            placeholder="Confirm new password"
                                            value={confirmNewOrgPassword}
                                            onChange={(e) => setConfirmNewOrgPassword(e.target.value)}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Button
                                variant="primary"
                                type="submit"
                                disabled={organisationAccountsLoading || organisationAccounts.length === 0}
                            >
                                Update Organisation Password
                            </Button>
                        </Form>
                    </Card.Body>
                </Card>
            </section>

            <Row className="pt-5 pb-5">
                <h3>Student Accounts</h3>
                <hr className="mb-3"></hr>
                <Form onSubmit={createStudentAccountHandler}>
                    <Form.Group className="mb-3 mt-5" controlId="formStudentAccountMode">
                        <Form.Label>Action:</Form.Label>
                        <Form.Select
                            required
                            value={studentAccountMode}
                            onChange={(e) => setStudentAccountMode(e.target.value)}
                        >
                            <option value="existing">Grant access to existing student account</option>
                            <option value="create">Create new student account</option>
                        </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-3 mt-5" controlId="formStudentFullName">
                        <Form.Label>Student Full Name (new account only):</Form.Label>
                        <Form.Control
                            required={studentAccountMode === 'create'}
                            type="text"
                            value={studentFullName}
                            onChange={(e) => setStudentFullName(e.target.value)}
                            placeholder="Enter student full name"
                        />
                    </Form.Group>
                    <Form.Group className="mb-3 mt-5" controlId="formStudentEmail">
                        <Form.Label>Student Email (username):</Form.Label>
                        {studentAccountMode === 'existing' ? (
                            <Form.Select
                                required
                                value={studentEmail}
                                onChange={(e) => setStudentEmail(e.target.value)}
                                disabled={studentAccounts.length === 0}
                            >
                                {studentAccounts.length === 0 ? (
                                    <option value="">No student accounts found</option>
                                ) : (
                                    studentAccounts.map((student) => (
                                        <option key={student.email} value={student.email}>
                                            {student.email}
                                        </option>
                                    ))
                                )}
                            </Form.Select>
                        ) : (
                            <Form.Control
                                required
                                type="email"
                                value={studentEmail}
                                onChange={(e) => setStudentEmail(e.target.value)}
                                placeholder="Enter student email"
                            />
                        )}
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="formStudentYearLevelGroup">
                        <Form.Label>Student Course Year Level Group:</Form.Label>
                        <Form.Select
                            required
                            value={studentYearLevelGroup}
                            onChange={(e) => setStudentYearLevelGroup(e.target.value)}
                        >
                            <option value="5-6">5-6</option>
                            <option value="7-8">7-8</option>
                            <option value="9-10">9-10</option>
                        </Form.Select>
                    </Form.Group>
                    <p className="text-muted mb-4">
                        Grant access to an existing student account, or create a new student account with generated credentials and a 3-month course access period.
                    </p>
                    <Button variant="primary" type="submit">
                        {studentAccountMode === 'create' ? 'Create Student Account' : 'Grant Student Access'}
                    </Button>
                </Form>

                <h4 className="mt-5">Current Student Accounts</h4>
                <hr className="mb-3"></hr>
                {subscriptionsLoading ? (
                    <Spinner animation="border" role="status">
                        <span className="sr-only">Loading...</span>
                    </Spinner>
                ) : (
                    <Table striped bordered hover responsive>
                        <thead>
                            <tr>
                                <th>Student</th>
                                <th>Email</th>
                                <th>Year Level Group</th>
                                <th>Expiry Date</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {studentAccounts.map((student) => {
                                const expiryDate = student.studentCourseAccessExpiryDate
                                    ? new Date(student.studentCourseAccessExpiryDate)
                                    : null;
                                const formattedExpiryDate = expiryDate && !Number.isNaN(expiryDate.getTime())
                                    ? `${String(expiryDate.getDate()).padStart(2, "0")}/${String(expiryDate.getMonth() + 1).padStart(2, "0")}/${expiryDate.getFullYear()}`
                                    : "-";
                                const hasActiveSubscription = Boolean(
                                    student.studentCourseYearLevelGroup &&
                                    expiryDate &&
                                    !Number.isNaN(expiryDate.getTime()) &&
                                    expiryDate > new Date()
                                );

                                return (
                                    <tr key={student.email}>
                                        <td>{student.fullName || "-"}</td>
                                        <td>{student.email}</td>
                                        <td>{student.studentCourseYearLevelGroup || "-"}</td>
                                        <td>{formattedExpiryDate}</td>
                                        <td>{hasActiveSubscription ? "Active" : "Inactive"}</td>
                                    </tr>
                                );
                            })}
                            {studentAccounts.length === 0 && (
                                <tr>
                                    <td colSpan={5} className="text-center">No student accounts found.</td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                )}
            </Row>
        </Container>
    )
}

const ScheduleComponent = ({ blockTimeHandler, unblockTimeHandler, bookedLessons }) => {
    const timeSlots = ["08:00", "09:00", "10:00", "11:00", "12:00", "13:00", "14:00", "15:00", "16:00", "17:00", "18:00", "19:00", "20:00", "21:00", "22:00"];
    const schedule = timeSlots.map((time) => {
        const lesson = bookedLessons.find((lesson) => {
            const lessonTime = lesson.date.split("T")[1].substring(0,5);
            return lessonTime === time;
        });

        if ( lesson ) {
            // Booking for an actual user, not the admin
            if ( lesson.user ) {
                // Booking for a module, not a free consultation
                if ( lesson.module ) {
                    return (
                        <div key={time} className="schedule-slot">
                            <p className="schedule-time">{time}</p>
                            <p className="schedule-meta">{lesson.module.name}</p>
                            <p>{lesson.user.fullName}, {lesson.user.email}, {lesson.user.group}</p>
                        </div>
                    );
                } else {
                    return (
                        <div key={time} className="schedule-slot">
                            <p className="schedule-time">{time}</p>
                            <p className="schedule-meta">Free Consultation</p>
                            <p>{lesson.user.fullName}, {lesson.user.email}, {lesson.user.group}</p>
                        </div>
                    );
                }
            } else {
                // Booking for admin, unblock option
                return (
                    <div key={time} className="schedule-slot">
                        <span className="schedule-time">{time}</span> <Button variant="outline-primary" size="sm" className="mx-2 my-2" onClick={() => unblockTimeHandler(lesson)}>Unblock</Button>
                        <p className="schedule-meta">{lesson.comment || "Blocked by admin"}</p>
                    </div>
                );
            }
        } else {
            return (
                <div key={time} className="schedule-slot schedule-slot-empty">
                    <span className="schedule-time">{time}</span> <Button variant="outline-secondary" size="sm" className="mx-2 my-2" onClick={() => blockTimeHandler(time)}>Block</Button>
                </div>
            );
        }
    });

    return <div className="schedule-list">{schedule}</div>;
};

export default AdminScreen;